import { useEffect, useRef } from "react";
import teamImage from "@/assets/22-studios-team-beautystudio-nuernberg.webp";

const About = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="ueber-uns" className="py-12 md:py-20 relative overflow-hidden">

      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-20 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-primary/20 via-primary/5 to-transparent blur-3xl animate-gentle-float" />
        <div className="absolute bottom-0 -left-40 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-blush/20 via-primary/10 to-transparent blur-3xl animate-drift" />
        <div className="absolute top-1/2 right-1/4 w-72 h-72 rounded-full bg-primary/10 blur-2xl animate-pulse-soft" />
        <div className="absolute top-20 right-20 w-48 h-48 border border-primary/10 rounded-full animate-spin-slow" />
        <div className="absolute bottom-32 left-1/3 w-64 h-64 border border-primary/8 rounded-full animate-spin-slow" style={{ animationDirection: 'reverse', animationDuration: '90s' }} />
        <div className="absolute top-1/4 left-10 w-3 h-3 rounded-full bg-primary/30 animate-float-particle" />
        <div className="absolute top-2/3 right-16 w-2 h-2 rounded-full bg-primary/40 animate-float-particle delay-200" />
        <div className="absolute bottom-1/4 left-1/2 w-4 h-4 rounded-full bg-blush/30 animate-float-particle delay-400" />
        <div className="absolute top-1/3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/10 to-transparent animate-shimmer-slow" />
      </div>

      <div className="container px-6 relative z-10">
        <div className="max-w-6xl mx-auto">

          {/* ── Glass Headline ── */}
          <div className="animate-on-scroll opacity-0 translate-y-8 text-center mb-10 md:mb-14">
            <div className="inline-block px-8 py-5 rounded-2xl bg-foreground/5 backdrop-blur-md border border-foreground/10">
              <div className="flex items-center justify-center gap-4 mb-3">
                <span className="w-12 h-px bg-gradient-to-r from-transparent to-highlight" />
                <span className="text-sm tracking-wide uppercase text-label font-medium">Wer wir sind</span>
                <span className="w-12 h-px bg-gradient-to-l from-transparent to-highlight" />
              </div>
              <h2 className="text-heading md:text-display font-serif font-light text-foreground">
                Über <span className="text-highlight font-medium">uns</span>
              </h2>
            </div>
          </div>

          {/* ── Main content: Image + Glass cards ── */}
          <div className="animate-on-scroll opacity-0 translate-y-8 grid md:grid-cols-12 gap-6 items-start">

            {/* Image block – 7 cols */}
            <div className="md:col-span-7 relative group">
              <div className="overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src={teamImage}
                  alt="Das Team von 22 Studios – Beauty-Expertinnen im Beautystudio Nürnberg"
                  className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
            </div>

            {/* Right side – Glass cards */}
            <div className="md:col-span-5 flex flex-col gap-4 md:pt-4">

              {/* Glass card 1: About text */}
              <div className="rounded-2xl bg-background/40 backdrop-blur-xl border border-border/50 p-6 md:p-8 shadow-lg">
                <div className="h-px w-10 bg-highlight mb-5" />
                <p className="text-base md:text-lg text-foreground font-light leading-[1.9]">
                  Bei <span className="font-medium text-foreground">22 Studios</span> verbinden wir
                  Fachwissen, Ästhetik und hochwertige Produkte zu einem ganzheitlichen Beauty-Erlebnis.
                </p>
                <p className="text-sm text-muted-foreground font-light leading-[1.8] mt-4">
                  Unser Anspruch ist es, jeden Besuch zu einem Moment der Entspannung und
                  Selbstfürsorge zu machen.
                </p>
              </div>

              {/* Glass card 2: Stats */}
              <div className="grid grid-cols-3 rounded-2xl bg-background/40 backdrop-blur-xl border border-border/50 shadow-lg overflow-hidden">
                {[
                  { value: "10+", label: "Jahre Erfahrung" },
                  { value: "5k+", label: "Zufriedene Kunden" },
                  { value: "20+", label: "Behandlungen" },
                ].map((s, i) => (
                  <div
                    key={s.label}
                    className={`px-4 md:px-6 py-5 text-center ${i < 2 ? "border-r border-border/50" : ""}`}
                  >
                    <span className="block text-2xl md:text-3xl font-serif font-light text-highlight">{s.value}</span>
                    <p className="text-[9px] md:text-[10px] tracking-widest uppercase text-muted-foreground mt-1">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Glass card 3: Decorative quote */}
              <div className="rounded-2xl bg-background/40 backdrop-blur-xl border border-border/50 p-6 shadow-lg">
                <p className="font-serif italic text-2xl md:text-3xl text-foreground/20 leading-tight select-none" aria-hidden>
                  Beauty. Expertise. Liebe.
                </p>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default About;
